import { useState } from 'react';
import { App, Button, Checkbox, DatePicker, Flex, Input, Segmented, Select, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { INVALID_INPUTS_HINT, InvalidInputsContext, MoneyInput, useInvalidInputs } from '@/shared/ui/inputs';
import { CounterpartySelect } from '@/entities/counterparty';
import {
  useCancelDocumentMutation,
  useCashOrderTemplateQuery,
  useCreateDocumentMutation,
  useDeleteDocumentMutation,
  useGetDocumentQuery,
  useListDocumentsQuery,
  usePostDocumentMutation,
  usePreviewDocumentMutation,
  useRestoreDocumentMutation,
  useReturnDocumentToDraftMutation,
  useUnpostDocumentMutation,
  useUpdateDocumentMutation,
} from '@/entities/document';
import type { DocumentCounterpartyRef, DocumentDto, LineWarningEntry, SaveDocumentPayload } from '@/entities/document';
import type { PaymentMethod } from '@/entities/payment';
import { useCashOrderPaymentQuery } from '@/entities/payment';
import { AllocationGrid, allocationRows, allocationSeedKey, allocationTarget, toManualAllocations } from '@/features/payment/allocation-grid';
import type { AllocationRow } from '@/features/payment/allocation-grid';
import { PreviewConsequencesDialog, useSaveWithPreview } from '@/features/document/preview-consequences';
import { isVersionConflict } from '@/features/document/concurrent-edit';
import { useStoreContext } from '@/shared/lib/store-context';
import { todayInTz } from '@/shared/lib/date';
import { describeWarnings } from '@/shared/lib/warnings';
import { translateApiError } from '@/shared/api/errorToMessage';
import type { CashOrderHeader, CashOrderType } from './model';
import { directionOf, isCashOrderComplete, windowTitle } from './model';

const METHOD_OPTIONS: { value: PaymentMethod; label: string }[] = [
  { value: 'CASH', label: 'Наличные' },
  { value: 'CASHLESS', label: 'Безналичные' },
  { value: 'TRANSFER', label: 'Перевод' },
];

export interface CashOrderWindowProps {
  type: CashOrderType;
  id?: string;
  /** §7 «Создать приходный/расходный ордер»: id продажи/прихода/возврата-основания. */
  baseDocumentId?: string;
  onClose: () => void;
}

/** §7: приходный/расходный кассовый ордер — полноценный документ (черновик → проведён →
 * отменён/удалён, как остальные), но без строк: сумма/способ/операция вводятся в шапке, а
 * разнесение по документам-должникам — той же сеткой, что и в `PaymentWindow`. */
export function CashOrderWindow({ type, id, baseDocumentId, onClose }: CashOrderWindowProps) {
  const { message, modal } = App.useApp();
  const ctx = useStoreContext();
  const [documentId, setDocumentId] = useState(id);
  const direction = directionOf(type);

  const { data: doc, isFetching, refetch: refetchDoc } = useGetDocumentQuery(documentId ?? '', { skip: !documentId });
  const [createDocument, { isLoading: isCreating }] = useCreateDocumentMutation();
  const [updateDocument, { isLoading: isUpdating }] = useUpdateDocumentMutation();
  const [postDocument, { isLoading: isPosting }] = usePostDocumentMutation();
  const [unpostDocument, { isLoading: isUnposting }] = useUnpostDocumentMutation();
  const [cancelDocument, { isLoading: isCancelling }] = useCancelDocumentMutation();
  const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation();
  const [returnToDraft, { isLoading: isReturningToDraft }] = useReturnDocumentToDraftMutation();
  const [restoreDocument, { isLoading: isRestoring }] = useRestoreDocumentMutation();
  const [previewDocument] = usePreviewDocumentMutation();
  const [isPosting_, setIsPosting_] = useState(false);
  const { runWithPreview, pendingDiff, resolvePending, isRunningPreview } = useSaveWithPreview();

  const [header, setHeader] = useState<CashOrderHeader>({
    docDate: todayInTz(ctx.timezone),
    amount: null,
    method: 'CASH',
    cashOperationType: 'PAYMENT',
    comment: '',
  });
  const [counterpartyRef, setCounterpartyRef] = useState<DocumentCounterpartyRef | undefined>(undefined);
  const [noAutoAllocate, setNoAutoAllocate] = useState(false);
  const [rows, setRows] = useState<AllocationRow[]>([]);
  const [rowsDirty, setRowsDirty] = useState(false);

  function seedHeaderFrom(d: DocumentDto) {
    setHeader({
      docDate: d.docDate.slice(0, 10),
      counterpartyId: d.counterpartyId ?? undefined,
      amount: d.totalAmount,
      method: (d.method as PaymentMethod | null) ?? 'CASH',
      cashOperationType: d.cashOperationType ?? 'PAYMENT',
      comment: d.comment ?? '',
    });
    setCounterpartyRef(d.counterparty ?? undefined);
  }

  // Загруженный документ → заполнить шапку один раз (render-phase, не эффект — тот же приём, что и
  // в DocumentWindow/PaymentWindow).
  const [seededDoc, setSeededDoc] = useState<DocumentDto | undefined>(undefined);
  if (doc && doc !== seededDoc) {
    setSeededDoc(doc);
    seedHeaderFrom(doc);
  }

  // §7 «Создать приходный/расходный ордер»: новое окно, открытое с основанием, — контрагент, вид
  // операции и сумма остатка долга подтягиваются один раз, тем же паттерном.
  const { data: template } = useCashOrderTemplateQuery(baseDocumentId ?? '', { skip: !baseDocumentId || !!id });
  const [seededTemplateFrom, setSeededTemplateFrom] = useState<string | undefined>(undefined);
  if (template && baseDocumentId && baseDocumentId !== seededTemplateFrom) {
    setSeededTemplateFrom(baseDocumentId);
    setHeader((h) => ({ ...h, counterpartyId: template.counterpartyId ?? undefined, cashOperationType: template.cashOperationType, amount: template.suggestedAmount }));
    setCounterpartyRef(template.counterparty ?? undefined);
  }

  const status = doc?.status ?? 'DRAFT';
  const isNew = !documentId;
  const isDeleted = !!doc?.deletedAt;
  const isReadOnly = status === 'CANCELLED' || isDeleted;
  const busy = isCreating || isUpdating || isPosting || isPosting_ || isUnposting || isCancelling || isDeleting || isReturningToDraft || isRestoring || isRunningPreview;

  // Разнесение и «Не разносить» не хранятся на документе, только на связанном Payment (§7).
  const { data: paymentData } = useCashOrderPaymentQuery(documentId ?? '', { skip: !documentId });
  const existingPayment = paymentData?.payment;
  const target = allocationTarget(header.cashOperationType, direction);
  const { data: sideList } = useListDocumentsQuery(
    { type: target.documentType, counterpartyIds: header.counterpartyId ? [header.counterpartyId] : [], statuses: ['POSTED'], paymentStatuses: ['UNPAID', 'PARTIAL'] },
    { skip: !header.counterpartyId },
  );
  const [seededSideKey, setSeededSideKey] = useState<string | undefined>(undefined);
  const sideKey = sideList
    ? allocationSeedKey(`${header.cashOperationType}:${direction}`, existingPayment?.rowVersion, sideList.items, existingPayment?.allocations ?? [])
    : undefined;
  if (sideKey && sideKey !== seededSideKey && !rowsDirty) {
    setSeededSideKey(sideKey);
    setRows(allocationRows(sideList!.items, existingPayment?.allocations ?? [], target, baseDocumentId, header.amount));
    setNoAutoAllocate(existingPayment?.doNotAllocate ?? false);
  }

  function editRows(next: AllocationRow[]) {
    setRowsDirty(true);
    setRows(next);
  }

  function handleMutationError(e: unknown) {
    if (!isVersionConflict(e)) {
      message.error(translateApiError(e));
      return;
    }
    modal.confirm({
      title: 'Документ изменён в другом окне',
      content: 'Кто-то успел сохранить более новую версию. Открыть свежую версию? Ваши несохранённые изменения будут потеряны.',
      okText: 'Открыть свежую версию',
      cancelText: 'Отмена',
      onOk: async () => {
        const result = await refetchDoc();
        if (result.data) seedHeaderFrom(result.data);
      },
    });
  }

  const invalidInputs = useInvalidInputs();
  const isComplete = isCashOrderComplete(header);
  const canSave = !invalidInputs.hasInvalid;
  const canPost = canSave && isComplete;

  function buildPayload(): SaveDocumentPayload {
    return {
      docDate: header.docDate,
      counterpartyId: header.counterpartyId,
      comment: header.comment || undefined,
      amount: header.amount ?? '0.00',
      method: header.method,
      cashOperationType: header.cashOperationType,
      noAutoAllocate: header.cashOperationType === 'PAYMENT' ? noAutoAllocate : false,
      allocations: toManualAllocations(rows),
    };
  }

  async function ensureCreated(): Promise<string> {
    if (documentId) return documentId;
    const created = await createDocument({ type, baseDocumentId, ...buildPayload() }).unwrap();
    setDocumentId(created.id);
    return created.id;
  }

  function refuseInvalidInput(): boolean {
    if (canSave) return false;
    message.warning(INVALID_INPUTS_HINT);
    return true;
  }

  async function handleSaveDraft(close: boolean) {
    if (refuseInvalidInput()) return;
    try {
      if (!documentId) {
        await ensureCreated();
      } else {
        await updateDocument({ id: documentId, rowVersion: doc!.rowVersion, body: buildPayload() }).unwrap();
        setRowsDirty(false);
      }
      message.success('Черновик сохранён');
      if (close) onClose();
    } catch (e) {
      handleMutationError(e);
    }
  }

  function warningTexts(warnings: LineWarningEntry[]): string[] {
    return describeWarnings(warnings, () => undefined);
  }

  function confirmWarnings(texts: string[]): Promise<boolean> {
    return new Promise((resolve) => {
      modal.confirm({
        title: 'Провести с предупреждениями?',
        content: (
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {texts.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        ),
        okText: 'Провести',
        cancelText: 'Не проводить',
        width: 480,
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  }

  async function handlePost(close: boolean) {
    if (isPosting_ || refuseInvalidInput() || !isComplete) return;
    setIsPosting_(true);
    try {
      const currentId = await ensureCreated();
      const rowVersion = documentId ? (doc?.rowVersion ?? 1) : 1;
      const payload = documentId ? buildPayload() : undefined;
      const preview = await previewDocument({ id: currentId, body: { action: 'post', rowVersion, document: payload } }).unwrap();
      const texts = warningTexts(preview.warnings);
      if (texts.length > 0 && !(await confirmWarnings(texts))) {
        message.info('Документ не проведён и остаётся черновиком');
        return;
      }
      const result = await postDocument({ id: currentId, body: { rowVersion, document: payload } }).unwrap();
      seedHeaderFrom(result.document);
      setRowsDirty(false);
      message.success(result.warnings.length > 0 ? 'Документ проведён с предупреждениями' : 'Документ проведён');
      if (close) onClose();
    } catch (e) {
      handleMutationError(e);
    } finally {
      setIsPosting_(false);
    }
  }

  async function handleSaveRepost(close: boolean) {
    if (!documentId || !doc || refuseInvalidInput()) return;
    const payload = buildPayload();
    try {
      const saved = await runWithPreview(
        documentId,
        'post',
        doc.rowVersion,
        async (stateVersion) => {
          const result = await postDocument({ id: documentId, body: { rowVersion: doc.rowVersion, document: payload, stateVersion } }).unwrap();
          seedHeaderFrom(result.document);
        },
        payload,
      );
      if (saved) {
        setRowsDirty(false);
        message.success('Документ перепроведён');
        if (close) onClose();
      }
    } catch (e) {
      handleMutationError(e);
    }
  }

  async function handleUnpost() {
    if (!documentId || !doc) return;
    try {
      const saved = await runWithPreview(documentId, 'unpost', doc.rowVersion, (stateVersion) =>
        unpostDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
      );
      if (saved) message.success('Проведение снято');
    } catch (e) {
      handleMutationError(e);
    }
  }

  function handleCancel() {
    if (!documentId || !doc) return;
    modal.confirm({
      title: 'Отменить документ?',
      onOk: async () => {
        try {
          const saved = await runWithPreview(documentId, 'cancel', doc.rowVersion, (stateVersion) =>
            cancelDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
          );
          if (saved) message.success('Документ отменён');
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  function handleDelete() {
    if (!documentId || !doc) return;
    modal.confirm({
      title: 'Удалить документ?',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const saved =
            status === 'POSTED'
              ? await runWithPreview(documentId, 'remove', doc.rowVersion, (stateVersion) =>
                  deleteDocument({ id: documentId, body: { rowVersion: doc.rowVersion, stateVersion } }).unwrap(),
                )
              : await deleteDocument({ id: documentId, body: { rowVersion: doc.rowVersion } })
                  .unwrap()
                  .then(() => true);
          if (saved) {
            message.success('Документ удалён');
            onClose();
          }
        } catch (e) {
          handleMutationError(e);
        }
      },
    });
  }

  async function handleReturnToDraft() {
    if (!documentId || !doc) return;
    try {
      const result = await returnToDraft({ id: documentId, rowVersion: doc.rowVersion }).unwrap();
      seedHeaderFrom(result.document);
      message.success('Документ возвращён в черновик');
    } catch (e) {
      handleMutationError(e);
    }
  }

  async function handleRestore() {
    if (!documentId || !doc) return;
    try {
      const result = await restoreDocument({ id: documentId, rowVersion: doc.rowVersion }).unwrap();
      seedHeaderFrom(result.document);
      message.success(result.document.status === 'DRAFT' ? 'Документ восстановлен как черновик — проведите его заново' : 'Документ восстановлен');
    } catch (e) {
      handleMutationError(e);
    }
  }

  return (
    <>
      <PreviewConsequencesDialog diff={pendingDiff} warningTexts={pendingDiff ? warningTexts(pendingDiff.warnings) : []} onResolve={resolvePending} />
      <FloatingWindow kind={`document:${type}`} title={windowTitle(type, doc, isNew)} onClose={onClose} footer={renderFooter()}>
        <InvalidInputsContext.Provider value={invalidInputs.api}>
          <Flex vertical gap={8}>
            {isNew ? (
              <Segmented
                value={header.cashOperationType}
                onChange={(v) => setHeader((h) => ({ ...h, cashOperationType: v as CashOrderHeader['cashOperationType'] }))}
                options={[
                  { value: 'PAYMENT', label: 'Оплата' },
                  { value: 'REFUND', label: 'Возврат денег' },
                ]}
              />
            ) : (
              <Typography.Text style={{ fontSize: 12 }} type="secondary">
                Операция: {header.cashOperationType === 'PAYMENT' ? 'Оплата' : 'Возврат денег'}
              </Typography.Text>
            )}

            <Flex gap={8} wrap>
              <Flex vertical style={{ minWidth: 220, flex: 1 }}>
                <Typography.Text style={{ fontSize: 12 }}>Контрагент</Typography.Text>
                <CounterpartySelect
                  value={header.counterpartyId}
                  current={counterpartyRef}
                  disabled={isReadOnly}
                  onChange={(v) => setHeader((h) => ({ ...h, counterpartyId: v }))}
                />
              </Flex>
              <Flex vertical style={{ minWidth: 140 }}>
                <Typography.Text style={{ fontSize: 12 }}>Дата</Typography.Text>
                <DatePicker
                  value={dayjs(header.docDate)}
                  format="DD.MM.YYYY"
                  disabled={isReadOnly}
                  onChange={(d) => setHeader((h) => ({ ...h, docDate: d ? d.format('YYYY-MM-DD') : h.docDate }))}
                />
              </Flex>
            </Flex>

            <Flex gap={8} wrap>
              <Flex vertical style={{ minWidth: 140 }}>
                <Typography.Text style={{ fontSize: 12 }}>Сумма</Typography.Text>
                <MoneyInput value={header.amount} disabled={isReadOnly} onChange={(v) => setHeader((h) => ({ ...h, amount: v }))} />
              </Flex>
              <Flex vertical style={{ minWidth: 160 }}>
                <Typography.Text style={{ fontSize: 12 }}>Способ</Typography.Text>
                <Select<PaymentMethod>
                  value={header.method}
                  disabled={isReadOnly}
                  options={METHOD_OPTIONS}
                  style={{ width: '100%' }}
                  onChange={(v) => setHeader((h) => ({ ...h, method: v }))}
                />
              </Flex>
              <Flex vertical style={{ minWidth: 200, flex: 1 }}>
                <Typography.Text style={{ fontSize: 12 }}>Комментарий</Typography.Text>
                <Input value={header.comment} disabled={isReadOnly} onChange={(e) => setHeader((h) => ({ ...h, comment: e.target.value }))} />
              </Flex>
            </Flex>

            {header.cashOperationType === 'PAYMENT' ? (
              <Checkbox checked={noAutoAllocate} disabled={isReadOnly} onChange={(e) => setNoAutoAllocate(e.target.checked)}>
                Не разносить (оставить свободным авансом)
              </Checkbox>
            ) : null}

            {header.counterpartyId && !(header.cashOperationType === 'PAYMENT' && noAutoAllocate) ? (
              <AllocationGrid
                title={target.title}
                remainderTitle={target.remainderTitle}
                emptyText={header.cashOperationType === 'REFUND' ? 'Нет незакрытых возвратов' : undefined}
                rows={rows}
                onChange={editRows}
              />
            ) : null}

            {invalidInputs.hasInvalid ? (
              <Typography.Text type="danger" style={{ fontSize: 12 }}>
                {INVALID_INPUTS_HINT}
              </Typography.Text>
            ) : null}
          </Flex>
        </InvalidInputsContext.Provider>
      </FloatingWindow>
    </>
  );

  function renderFooter() {
    if (isFetching && documentId) return <Typography.Text type="secondary">Загрузка…</Typography.Text>;

    if (isDeleted) {
      return (
        <Space wrap>
          <Button type="primary" loading={busy} onClick={() => void handleRestore()}>
            Восстановить
          </Button>
          <Button onClick={onClose}>Закрыть</Button>
        </Space>
      );
    }
    if (status === 'DRAFT') {
      return (
        <Space wrap>
          <Button type="primary" loading={busy} disabled={!canPost} onClick={() => void handlePost(true)}>
            Провести и закрыть
          </Button>
          <Button loading={busy} disabled={!canPost} onClick={() => void handlePost(false)}>
            Провести
          </Button>
          <Button loading={busy} disabled={!canSave} onClick={() => void handleSaveDraft(false)}>
            Сохранить черновик
          </Button>
          <Button danger disabled={!documentId} loading={busy} onClick={() => void handleDelete()}>
            Удалить
          </Button>
          <Button onClick={onClose}>Закрыть</Button>
        </Space>
      );
    }
    if (status === 'POSTED') {
      return (
        <Space wrap>
          <Button type="primary" loading={busy} disabled={!canPost} onClick={() => void handleSaveRepost(true)}>
            Сохранить и закрыть
          </Button>
          <Button loading={busy} disabled={!canPost} onClick={() => void handleSaveRepost(false)}>
            Сохранить
          </Button>
          <Button loading={busy} onClick={() => void handleUnpost()}>
            Снять проведение
          </Button>
          <Button loading={busy} onClick={() => void handleCancel()}>
            Отменить
          </Button>
          <Button danger loading={busy} onClick={() => void handleDelete()}>
            Удалить
          </Button>
          <Button onClick={onClose}>Закрыть</Button>
        </Space>
      );
    }
    // CANCELLED
    return (
      <Space wrap>
        <Button loading={busy} onClick={() => void handleReturnToDraft()}>
          Вернуть в черновик
        </Button>
        <Button onClick={onClose}>Закрыть</Button>
      </Space>
    );
  }
}
